import { useAuthViewport } from '@/epics/authentication/pages/login/AuthTemplate/useAuthViewport';
import { useState, useMemo, useCallback } from 'react';

interface UseLayoutReturn {
  // State
  isCollapsed: boolean;

  // Functions
  toggleCollapse: () => void;
  setCollapsed: (collapsed: boolean) => void;

  // Computed values
  sidebarWidth: string;
  logoIconSrc: string;
  logoTextSrc: string;

  // Style objects
  sidebarStyle: React.CSSProperties;
  headerStyle: React.CSSProperties;
  contentAreaStyle: React.CSSProperties;
  logoIconStyle: React.CSSProperties;
  logoTextStyle: React.CSSProperties;
}

const COLLAPSED_WIDTH = '88px';
const EXPANDED_WIDTH = '220px';
// Sidebar state lives in LayOut, which remounts whenever NavigationRoutes'
// <Routes key> changes (edit-client/add-client/edit-subscription stepper
// routes each use their own key) — persisting here survives that remount,
// same pattern as LayOut's own MODAL_DISMISSED_KEY.
const SIDENAV_COLLAPSED_KEY = 'sidenav-collapsed';

const readStoredCollapsed = (): boolean | null => {
  try {
    const stored = sessionStorage.getItem(SIDENAV_COLLAPSED_KEY);
    return stored === null ? null : stored === 'true';
  } catch {
    return null;
  }
};

const persistCollapsed = (collapsed: boolean): void => {
  try {
    sessionStorage.setItem(SIDENAV_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Storage unavailable (private browsing, etc.) — state just won't survive a remount.
  }
};
const LOGO_ICON_SRC = '/logoIcon.svg';
const LOGO_TEXT_SRC = '/logoText.svg';
// Matches the Figma logo lockup's native height exactly — logoIcon.svg
// (43x39) and logoText.svg (123x39) are both natively sized for a 39px-tall
// render, so this renders them at 1:1 scale instead of stretching ~13%
// beyond their source artwork.
const LOGO_HEIGHT = '39px';
const LABEL_HEIGHT = '56px';
const HEADER_HEIGHT = '64px';

export const useLayout = (): UseLayoutReturn => {
  const { isTablet } = useAuthViewport();
  const [isCollapsed, setIsCollapsed] = useState(() =>
    isTablet ? true : (readStoredCollapsed() ?? false)
  );

  // Functions
  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      persistCollapsed(next);
      return next;
    });
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
    persistCollapsed(collapsed);
  }, []);

  // Computed values
  const sidebarWidth = useMemo(
    () => (isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH),
    [isCollapsed]
  );

  // Style objects
  const sidebarStyle = useMemo<React.CSSProperties>(
    () => ({
      width: sidebarWidth,
    }),
    [sidebarWidth]
  );

  const headerStyle = useMemo<React.CSSProperties>(
    () => ({
      marginLeft: sidebarWidth,
      width: `calc(100vw - ${sidebarWidth})`,
    }),
    [sidebarWidth]
  );

  const contentAreaStyle = useMemo<React.CSSProperties>(
    () => ({
      marginLeft: sidebarWidth,
      width: `calc(100vw - ${sidebarWidth})`,
    }),
    [sidebarWidth]
  );

  const logoIconStyle = useMemo<React.CSSProperties>(
    () => ({
      height: LOGO_HEIGHT,
      width: 'auto',
      objectFit: 'contain' as const,
    }),
    []
  );

  const logoTextStyle = useMemo<React.CSSProperties>(
    () => ({
      height: LOGO_HEIGHT,
      width: 'auto',
      objectFit: 'contain' as const,
    }),
    []
  );

  return {
    // State
    isCollapsed,

    // Functions
    toggleCollapse,
    setCollapsed,

    // Computed values
    sidebarWidth,
    logoIconSrc: LOGO_ICON_SRC,
    logoTextSrc: LOGO_TEXT_SRC,

    // Style objects
    sidebarStyle,
    headerStyle,
    contentAreaStyle,
    logoIconStyle,
    logoTextStyle,
  };
};

// Export constants for use in component if needed
export const LAYOUT_CONSTANTS = {
  COLLAPSED_WIDTH,
  EXPANDED_WIDTH,
  LOGO_HEIGHT,
  LABEL_HEIGHT,
  HEADER_HEIGHT,
};
